import Phaser from 'phaser';
import { GAME_WIDTH } from '@hokm/shared';
import { PERSIAN_THEME } from '../../../theme/persianTheme';
import { UrlStateManager } from '../../../utils/UrlStateManager';

export interface RoomCodeDisplayCallbacks {
  getVanityRoomId: () => string | null;
}

/**
 * Displays the room code with click-to-copy/share functionality.
 */
export class RoomCodeDisplay {
  private container: Phaser.GameObjects.Container;
  private roomCodeText: Phaser.GameObjects.Text;
  private copiedFeedback: Phaser.GameObjects.Text;
  private hint: Phaser.GameObjects.Text;

  constructor(
    private scene: Phaser.Scene,
    private callbacks: RoomCodeDisplayCallbacks
  ) {
    this.container = this.scene.add.container(GAME_WIDTH / 2, 215);
    this.roomCodeText = this.createRoomCodeText();
    this.copiedFeedback = this.createCopiedFeedback();
    this.hint = this.createHint();

    this.buildDisplay();
    this.setupInteraction();
    this.container.setVisible(false);
  }

  private createRoomCodeText(): Phaser.GameObjects.Text {
    return this.scene.add
      .text(0, 6, '', {
        fontFamily: 'Consolas, Monaco, monospace',
        fontSize: '18px',
        color: PERSIAN_THEME.hex.gold,
        fontStyle: 'bold',
        letterSpacing: 3,
      })
      .setOrigin(0.5);
  }

  private createCopiedFeedback(): Phaser.GameObjects.Text {
    return this.scene.add
      .text(0, 28, 'Copied!', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '10px',
        color: PERSIAN_THEME.hex.textHighlight,
      })
      .setOrigin(0.5)
      .setAlpha(0);
  }

  private createHint(): Phaser.GameObjects.Text {
    return this.scene.add
      .text(0, 28, 'Click to copy', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '9px',
        color: PERSIAN_THEME.hex.textSecondary,
      })
      .setOrigin(0.5)
      .setAlpha(0.6);
  }

  private buildDisplay(): void {
    // Background with rounded rectangle
    const bg = this.scene.add.graphics();
    bg.fillStyle(PERSIAN_THEME.colors.tableCenter, 0.95);
    bg.fillRoundedRect(-90, -22, 180, 44, 8);
    bg.lineStyle(1, PERSIAN_THEME.colors.gold, 0.8);
    bg.strokeRoundedRect(-90, -22, 180, 44, 8);

    // "ROOM CODE" label
    const label = this.scene.add
      .text(0, -10, 'ROOM CODE', {
        fontFamily: PERSIAN_THEME.fonts.body,
        fontSize: '9px',
        color: PERSIAN_THEME.hex.textSecondary,
        letterSpacing: 1,
      })
      .setOrigin(0.5);

    this.container.add([bg, label, this.roomCodeText, this.hint, this.copiedFeedback]);
  }

  private setupInteraction(): void {
    this.container.setSize(180, 44);
    this.container.setInteractive({ useHandCursor: true });

    this.container.on('pointerover', () => {
      this.hint.setAlpha(1);
      this.roomCodeText.setColor(PERSIAN_THEME.hex.textHighlight);
    });

    this.container.on('pointerout', () => {
      this.hint.setAlpha(0.6);
      this.roomCodeText.setColor(PERSIAN_THEME.hex.gold);
    });

    this.container.on('pointerdown', () => this.handleCopy());
  }

  private async handleCopy(): Promise<void> {
    const code = this.roomCodeText.text;
    if (!code) return;

    // Generate shareable link (uses vanity if set)
    const vanityRoomId = this.callbacks.getVanityRoomId();
    const shareLink = UrlStateManager.generateShareLink(code, !!vanityRoomId);

    try {
      // Try native share API on mobile
      if (navigator.share) {
        await navigator.share({
          title: 'Join my Hokm game!',
          text: `Room: ${code}`,
          url: shareLink,
        });
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(shareLink);
      }
    } catch (err) {
      // User cancelled share or clipboard failed - try clipboard as fallback
      try {
        await navigator.clipboard.writeText(shareLink);
      } catch {
        // Ignore clipboard errors
      }
    }

    // Show "Copied!" feedback
    this.hint.setVisible(false);
    this.copiedFeedback.setAlpha(1);

    this.scene.time.delayedCall(1500, () => {
      this.copiedFeedback.setAlpha(0);
      this.hint.setVisible(true);
    });
  }

  setRoomCode(code: string): void {
    this.roomCodeText.setText(code);
  }

  setVisible(visible: boolean): void {
    this.container.setVisible(visible);
  }

  destroy(): void {
    this.container.destroy();
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }
}
